import { sha256Hex, sniffFontMime } from '../images/bytes';
import { uid } from '../lib/id';
import { store } from '../state/store';
import { getAsset, putAsset } from '../storage/repo';
import { registerCustomFont, type FontDef } from './fonts';

const MAX_FONT_BYTES = 10 * 1024 * 1024;

async function activate(id: string, family: string, data: ArrayBuffer): Promise<FontDef> {
  const face = new FontFace(family, data);
  await face.load();
  document.fonts.add(face);
  return registerCustomFont(id, family);
}

export async function addCustomFontFromFile(file: File): Promise<FontDef> {
  if (file.size > MAX_FONT_BYTES) throw new Error('Font files must be smaller than 10 MB.');
  const data = await file.arrayBuffer();
  const mime = sniffFontMime(new Uint8Array(data.slice(0, 4)));
  if (!mime) throw new Error('That file is not a TrueType, OpenType or WOFF font.');
  const base = file.name.replace(/\.(ttf|otf|woff2?)$/i, '').replace(/[^\p{L}\p{N} _-]/gu, '').trim().slice(0, 40) || 'Custom font';
  const id = uid('f');
  const family = `MN ${base}`;
  let def: FontDef;
  try {
    def = await activate(id, family, data);
  } catch {
    throw new Error('This font could not be loaded by your browser.');
  }
  const asset = await putAsset({ id: uid('a'), kind: 'font', mime, name: file.name.slice(0, 200), size: data.byteLength, hash: await sha256Hex(data), createdAt: Date.now(), data });
  const fonts = store.get().settings.customFonts;
  store.updateSettings({ customFonts: [...fonts, { id, family, assetId: asset.id }] });
  return def;
}

/** Re-register stored custom fonts at startup. */
export async function loadCustomFonts(): Promise<void> {
  for (const f of store.get().settings.customFonts) {
    try {
      const a = await getAsset(f.assetId);
      if (a) await activate(f.id, f.family, a.data);
    } catch {
      /* a broken font file should not stop the app */
    }
  }
}
